import { FC, Fragment, useEffect, useRef, useState } from 'react'

import { ApplicationStatus } from 'commons/types/enums'
import { PageSectionPrimary, PageSectionSecondary, Toggle } from 'ui/components'

import { Questionnaire } from '../Questionnaire'
import { ApproveCompanyDialog } from './ApproveCompanyDialog'
import { CompanyDetailsProps } from './CompanyDetailsProps'
import { DenyCompanyDialog } from './DenyCompanyDialog'
import { CompanyInformation, ContactsInformation, LicenseInformation } from './components'
import { useCompanyDetails } from './useCompanyDetails'

import { s } from './styles'

export const CompanyDetails: FC<CompanyDetailsProps> = ({ application }) => {
  const { applicationID, relationships, progressTimelineStatus } = application
  const initialIndex = relationships.length > 1 ? 0 : -1
  const [expandedCompany, setExpandedCompany] = useState(initialIndex)
  const firstRender = useRef<boolean>(true)
  const prevStatus = useRef<ApplicationStatus | null>(progressTimelineStatus)

  // MP-7001 after click "Send Document Request" companies items should collapse
  const checkExpanded = (index: number) => {
    // status changed to "ApplicationStatus.DOCUMENT_REQUEST_PENDING"
    const isChangedToCollapseStatus =
      prevStatus.current !== ApplicationStatus.DOCUMENT_REQUEST_PENDING &&
      progressTimelineStatus === ApplicationStatus.DOCUMENT_REQUEST_PENDING

    // first render and status "ApplicationStatus.DOCUMENT_REQUEST_PENDING"
    // => checkExpanded => true
    const isFirstRenderCollapseStatus =
      firstRender.current && progressTimelineStatus === ApplicationStatus.DOCUMENT_REQUEST_PENDING

    const isShouldCollapsed = isChangedToCollapseStatus || isFirstRenderCollapseStatus

    const originalCondition = expandedCompany === index
    const result = isShouldCollapsed ? false : originalCondition

    return result
  }

  useEffect(() => {
    firstRender.current = false
  }, [])

  useEffect(() => {
    prevStatus.current = progressTimelineStatus
  }, [progressTimelineStatus])

  const { currentCompanyIndex, setCurrentCompanyIndex } = useCompanyDetails()

  const onDenyCompany = ({ companyIndex }: { companyIndex: number }) => {
    setCurrentCompanyIndex(companyIndex)
  }

  return (
    <PageSectionPrimary title="Company Details" face="solid">
      <s.CompanyWrapper>
        {relationships.map((company, index) => {
          const { isDenied, enableDenyCompany, licenses, contacts, questionnaire } = company

          return (
            <Fragment key={index}>
              {currentCompanyIndex === index && !isDenied && (
                <DenyCompanyDialog
                  onClose={() => setCurrentCompanyIndex(-1)}
                  applicationId={applicationID}
                  companyIndex={currentCompanyIndex}
                />
              )}
              {currentCompanyIndex === index && isDenied && (
                <ApproveCompanyDialog
                  onClose={() => setCurrentCompanyIndex(-1)}
                  applicationId={applicationID}
                  companyIndex={currentCompanyIndex}
                />
              )}
              <PageSectionSecondary
                foldable
                title={`Company #${index + 1}`}
                face="solid"
                anchor={`company_${index + 1}`}
                expanded={checkExpanded(index)}
                onToggle={(expanded) => {
                  if (!expanded) {
                    setExpandedCompany(-1)
                  } else {
                    setExpandedCompany(index)
                  }
                }}
                actions={
                  (isDenied || (!isDenied && enableDenyCompany)) && (
                    <s.DenyButton>
                      <Toggle value={!isDenied} onChange={() => onDenyCompany({ companyIndex: index })} />
                      <span>{isDenied ? 'Denied' : 'Approved'}</span>
                    </s.DenyButton>
                  )
                }
              >
                <CompanyInformation applicationRelationship={company} />
                {licenses?.length > 0 && <LicenseInformation licenses={licenses} />}
                {contacts?.length > 0 && <ContactsInformation contacts={contacts} />}
                {questionnaire && <Questionnaire questionnaire={questionnaire} />}
              </PageSectionSecondary>
            </Fragment>
          )
        })}
      </s.CompanyWrapper>
    </PageSectionPrimary>
  )
}
